import {uuidv4} from '@shared-types';
import {AgentAction, EmailAction, TriggerActionEntity} from '../action/trigger-action.entity';
import {TriggerEventType} from '../event/trigger.event.types';
import {AbstractTriggerEntity} from '../trigger.entity';
import {TriggerEntityClassMetadata} from '../trigger.types';
import {CycleStatusType} from '../../cycles/cycle/types/cycle-status.type';
import {instanceToPlain} from 'class-transformer';
import {IsEnum} from 'class-validator';

const CycleStatusSharedMetadata: Pick<TriggerEntityClassMetadata, 'availableActionClasses' | 'type'> = {
	type: TriggerEventType.CycleStatus,
	availableActionClasses: [AgentAction, EmailAction],
};

export class BaseCycleStatusTriggerEntity extends AbstractTriggerEntity {
	static override _uniqueId: string = uuidv4();

	/* -------------------  Value ------------------- */
	public static readonly CycleStatusTriggerType = {
		[CycleStatusType.Cycling]: 2,
		[CycleStatusType.Completing]: 3,
		[CycleStatusType.Denying]: 5,
		[CycleStatusType.Canceling]: 7,
	} as const;
	@IsEnum(BaseCycleStatusTriggerEntity.CycleStatusTriggerType, {message: 'trigger.errors.invalidCycleStatusValue'}) public value: KeyOrValueOf<
		typeof BaseCycleStatusTriggerEntity.CycleStatusTriggerType
	>;

	/* ------------------- Action ------------------- */
	action: EmailAction | AgentAction;

	protected constructor(init?: Partial<BaseCycleStatusTriggerEntity>) {
		super(TriggerEventType.CycleStatus, init);
		if (init?.action) this.action = TriggerActionEntity.create<EmailAction | AgentAction>(init?.action?.type, init?.action);
	}

	public static readonly is: (trigger: any) => trigger is BaseCycleStatusTriggerEntity = (trigger): trigger is BaseCycleStatusTriggerEntity => {
		return trigger instanceof BaseCycleStatusTriggerEntity || (trigger?.event === TriggerEventType.CycleStatus && trigger?.value !== undefined);
	};

	public static create<T extends BaseCycleStatusTriggerEntity>(init?: Partial<T>): T {
		if (!BaseCycleStatusTriggerEntity.is(init)) throw new Error('Invalid event type');
		switch (init?.value) {
			case BaseCycleStatusTriggerEntity.CycleStatusTriggerType.Completing:
				return new CycleCompletingTrigger(init) as T;
			case BaseCycleStatusTriggerEntity.CycleStatusTriggerType.Denying:
				return new CycleDenyingTrigger(init) as T;
			case BaseCycleStatusTriggerEntity.CycleStatusTriggerType.Canceling:
				return new CycleCancelingTrigger(init) as T;
			default:
				throw new Error(`Unsupported cycle status type: ${init.value}`);
		}
	}

	toJSON(): BaseCycleStatusTriggerEntity {
		return instanceToPlain(this, {}) as BaseCycleStatusTriggerEntity;
	}
}

/* -------------------------------------------------------------------------------------------------------------- */
// TODO:
// class CycleCyclingTrigger extends BaseCycleStatusTriggerEntity {
// 	static override _uniqueId: string = uuidv4();
// 	constructor(init?: Partial<BaseCycleStatusTriggerEntity>) {
// 		super({...init, value: BaseCycleStatusTriggerEntity.CycleStatusTriggerType.Cycling});
// 	}
// }

@GroupDecorator('trigger-entity', true)
@ClassMetaData<TriggerEntityClassMetadata>({
	name: 'cycle-completing-trigger',
	label: 'components.trigger-editor.event.cycle-completing.summary',
	type: TriggerEventType.CycleStatus,
	summary: 'components.trigger-editor.event.cycle-completing.summary',
	...CycleStatusSharedMetadata,
})
export class CycleCompletingTrigger extends BaseCycleStatusTriggerEntity {
	static override _uniqueId: string = uuidv4();

	constructor(init?: Partial<BaseCycleStatusTriggerEntity>) {
		super({...init, value: BaseCycleStatusTriggerEntity.CycleStatusTriggerType.Completing});
	}
}

@GroupDecorator('trigger-entity', true)
@ClassMetaData<TriggerEntityClassMetadata>({
	name: 'cycle-denying-trigger',
	label: 'components.trigger-editor.event.cycle-denying.summary',
	summary: 'components.trigger-editor.event.cycle-denying.summary',
	...CycleStatusSharedMetadata,
})
export class CycleDenyingTrigger extends BaseCycleStatusTriggerEntity {
	static override _uniqueId: string = uuidv4();

	constructor(init?: Partial<BaseCycleStatusTriggerEntity>) {
		super({...init, value: BaseCycleStatusTriggerEntity.CycleStatusTriggerType.Denying});
	}
}

@GroupDecorator('trigger-entity', true)
@ClassMetaData<TriggerEntityClassMetadata>({
	name: 'cycle-canceling-trigger',
	label: 'components.trigger-editor.event.cycle-canceling.summary',
	summary: 'components.trigger-editor.event.cycle-canceling.summary',
	...CycleStatusSharedMetadata,
})
export class CycleCancelingTrigger extends BaseCycleStatusTriggerEntity {
	static override _uniqueId: string = uuidv4();

	constructor(init?: Partial<BaseCycleStatusTriggerEntity>) {
		super({...init, value: BaseCycleStatusTriggerEntity.CycleStatusTriggerType.Canceling});
	}
}
